import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AccessPicker.module.css";
import Loader from "../Loader/Loader";
import { getMyTeams, switchContext, setContactOwnerId } from "../../Store/apiStore";
import { applyContext, getActiveOwnerId, getSelfId } from "../../lib/activeContext";
import { useTeamStore } from "../../Store/teamStore";

// AccessPicker — "which account am I working in?" chosen once, right after login.
//fg
// One person can hold three kinds of access: their OWN account, ADMIN on some
// owner's account, and SUPPORT on another's. Before this screen the login picked
// one for them (resolveTeamMembership's first row) and there was no way back.UPDATE
//
// The choice is cdddapplied by the SERVER (POST /team/switch-context re-signs the
// JWT). We never just write an ownerId into storage: the token carries the role
// and permissions the backend enforces against, so a client-side-only switch
// would leave every server check answering for the previous account.
//
// Renders nothing and forwards automatically when there is only one context —
// the overwhelmingly common case — so normal users never see an extra tap.

const ROLE_LABEL = { owner: "Your account", admin: "Admin", support: "Support" };
const ROLE_CLASS = {
  owner: styles.badgeOwner,
  admin: styles.badgeAdmin,
  support: styles.badgeSupport,
};

const initials = (label = "") =>
  label
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("") || "?";

/**
 * @param {object}   props
 * @param {function} props.onResolved  called with the chosen ownerId once the
 *                   token has been swapped. Owns the navigation decision, so
 *                   the picker itself never hard-codes a destination.
 * @param {boolean}  [props.allowSingle] render even when there is one option
 *                   (used by the in-app switcher, where the user asked for it).
 */
const AccessPicker = ({ onResolved, allowSingle = false }) => {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const resetTeams = useTeamStore((s) => s.reset);

  const choose = useCallback(
    async (ownerId) => {
      if (busy) return;
      setBusy(true);
      setError("");
      try {
        const { token } = await switchContext(ownerId);
        applyContext(token, ownerId, { setContactOwnerId });
        resetTeams(); // memberships were loaded for the previous context
        onResolved?.(ownerId);
      } catch (err) {
        setBusy(false);
        setError(
          err?.response?.data?.error ||
            "Could not open that account. Please try again."
        );
      }
    },
    [busy, onResolved, resetTeams]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getMyTeams();
        if (cancelled) return;

        const self = data?.self;
        const teams = data?.teams || [];
        // `self.available` is decided server-side: a support member with no
        // agents of their own has no workspace to switch into, and offering it
        // would drop them into the create-an-agent flow.
        const list = [
          ...(self?.available
            ? [
                {
                  ownerId: self.ownerId,
                  title: "My account",
                  meta:
                    self.agentCount > 0
                      ? `${self.agentCount} agent${self.agentCount > 1 ? "s" : ""}`
                      : "Your own workspace",
                  role: "owner",
                  isSelf: true,
                },
              ]
            : []),
          ...teams.map((t) => ({
            ownerId: t.ownerId,
            title:
              t.ownerBusiness || t.ownerCompany || t.ownerName || "Account",
            // Owner ki email picker me nahi dikhani.
            meta: t.ownerName || "",
            role: t.role === "admin" ? "admin" : "support",
            isSelf: false,
          })),
        ];

        // Nothing to choose between → don't make them tap. Still route through
        // switch-context when the single option is not what the token already
        // holds, so the session ends up in a verified state either way.
        if (!allowSingle && list.length <= 1) {
          // ALWAYS go through choose(): switch-context is what stamps
          // `ctxPicked` on the token. Returning via onResolved would leave the
          // token unstamped, and resolveHomeRoute would bounce straight back
          // here — an infinite redirect, and precisely for the commonest case
          // of all: a support member belonging to exactly one team.
          const only = list[0]?.ownerId || getSelfId();
          if (only) await choose(only);
          else onResolved?.(null);
          return;
        }

        setOptions(list);
      } catch (err) {
        if (cancelled) return;
        // The picker must never be a dead end: on failure fall through to the
        // context the token already carries.
        console.error("[AccessPicker] load failed:", err?.message);
        onResolved?.(getActiveOwnerId());
      }
    })();

    return () => {
      cancelled = true;
    };
    // `choose` is stable per busy-state; re-running on it would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowSingle]);

  if (!options) {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.containerBox}>
      <div className={styles.container}>
        <div className={styles.titleBar}>
          <span className={styles.lightTitle}>Choose your </span>
          <span className={styles.boldTitle}>access</span>
        </div>
        <p className={styles.subtitle}>
          You have access to more than one account. Pick the one you want to work
          in — you can switch any time from your profile.
        </p>

        <div className={styles.list}>
          {options.map((o) => (
            <button
              key={o.ownerId}
              type="button"
              className={styles.card}
              disabled={busy}
              onClick={() => choose(o.ownerId)}
            >
              <span
                className={`${styles.avatar} ${o.isSelf ? styles.avatarSelf : ""}`}
              >
                {initials(o.title)}
              </span>
              <span className={styles.cardBody}>
                <span className={styles.cardTitle}>{o.title}</span>
                {o.meta ? <span className={styles.cardMeta}>{o.meta}</span> : null}
              </span>
              <span className={`${styles.badge} ${ROLE_CLASS[o.role]}`}>
                {ROLE_LABEL[o.role]}
              </span>
            </button>
          ))}
        </div>

        {error ? <div className={styles.error}>{error}</div> : null}

        {allowSingle && (
          <div style={{ textAlign: "center", marginTop: "16px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                background: "none",
                border: "none",
                color: "#6524eb",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              &larr; Back to profile
            </button>
          </div>
        )}

        <p className={styles.footNote}>
          Support access is read-only unless the account owner grants more.
        </p>
      </div>
    </div>
  );
};

export default AccessPicker;
